import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import AuditHolds from './AuditHolds.jsx';
import { usePublicConfig } from '../../config/demo.js';
import './customer-dispute-adjustment-console.scss';

const BLUE = { color: '#005293' };
const PAGE_SIZE = 4;
const STATUS_BADGE = {
  UnderReview: ['badge-warning-soft', 'Under Review'],
  Adjusted: ['badge-success-soft', 'Adjusted'],
  Rejected: ['badge-danger-soft', 'Rejected'],
};
const ADJ_CATEGORY = { RefundIdle: 'Idle Fee Waiver', RefundFull: 'Full Session Waiver', Custom: 'Custom Goodwill Credit' };
const php = (v) => `₱${Number(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CustomerDisputeAdjustmentConsole() {
  const { notificationsSimulated } = usePublicConfig();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('ALL');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const [adjType, setAdjType] = useState('RefundIdle');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  const [telemetry, setTelemetry] = useState(null);
  const [ledger, setLedger] = useState(null);

  const flash = (ok, msg) => {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadList = useCallback(() => api.listDisputes({ page, pageSize: PAGE_SIZE, q: query, status: status === 'ALL' ? '' : status })
    .then((r) => { setItems(r.data); setTotal(r.meta.total); setPending(r.meta.pending); setError(''); return r.data; })
    .catch((e) => { setError(e.message); return []; }), [page, query, status]);

  useEffect(() => { loadList(); }, [loadList]);

  // Auto-select the first case when nothing (or a case no longer on the page) is selected.
  useEffect(() => {
    if (items.length > 0 && !items.some((d) => d.id === selectedId) && !detail) setSelectedId(items[0].id);
    if (items.length === 0) { setSelectedId(null); setDetail(null); }
  }, [items, selectedId, detail]);

  const resetForm = (d) => {
    setAdjType('RefundIdle');
    setAmount(d.cdr.idleCost > 0 ? String(d.cdr.idleCost) : '');
    setNotes('');
    setFormError('');
  };

  useEffect(() => {
    if (!selectedId) return;
    api.getDispute(selectedId).then((d) => { setDetail(d); resetForm(d); }).catch((e) => setError(e.message));
  }, [selectedId]);

  const pickType = (t) => {
    setAdjType(t);
    if (!detail) return;
    if (t === 'RefundIdle') setAmount(String(detail.cdr.idleCost));
    if (t === 'RefundFull') setAmount(String(detail.cdr.totalAmount));
  };

  const validate = () => {
    if (!notes.trim()) return 'Please enter an auditable justification note.';
    const a = Number(amount);
    if (!amount || Number.isNaN(a) || a <= 0) return 'Enter a credit amount greater than zero.';
    if (a > detail.policyThreshold) return `Adjustments above ${php(detail.policyThreshold)} exceed the customer care policy threshold.`;
    if (a > detail.cdr.totalAmount) return `Credit cannot exceed the billed amount of ${php(detail.cdr.totalAmount)}.`;
    return '';
  };

  const afterResolve = async (updated, msg) => {
    setDetail(updated);
    flash(true, msg);
    await loadList();
  };

  const apply = async () => {
    const v = validate();
    setFormError(v);
    if (v) return;
    setBusy(true);
    try {
      const updated = await api.adjustDispute(detail.id, { category: ADJ_CATEGORY[adjType], amount: Number(amount), justification: notes.trim() });
      await afterResolve(updated, `Double-entry ledger adjustment posted. ${php(updated.resolution.amount)} credited to the driver wallet${notificationsSimulated ? '. Demo: the driver notice is recorded, not delivered.' : ' and a notice was sent.'}`);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!notes.trim()) { setFormError('Please enter an auditable justification note.'); return; }
    if (!window.confirm('Are you sure you want to reject this dispute?')) return;
    setBusy(true);
    try {
      const updated = await api.rejectDispute(detail.id, { justification: notes.trim() });
      await afterResolve(updated, notificationsSimulated ? 'Dispute rejected. Demo: the driver notice is recorded, not delivered.' : 'Dispute rejected. Customer notification sent.');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openTelemetry = async () => {
    try { setTelemetry(await api.getDisputeTelemetry(detail.id)); } catch (e) { flash(false, e.message); }
  };
  const openLedger = async () => {
    try { setLedger(await api.getDisputeLedger(detail.id)); } catch (e) { flash(false, e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const resolved = detail && detail.status !== 'UnderReview';
  const c = detail?.cdr;

  return (
    <div className="container-fluid py-4" id="dispute-console-container">
      {toast && <div className={`toast-lite alert ${toast.ok ? 'alert-success' : 'alert-danger'} shadow`} role="status" id="dispute-toast">{toast.msg}</div>}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={BLUE}>
            <i className="fas fa-headset me-2" style={BLUE} />Customer Dispute &amp; Billing Adjustment Console
          </h4>
          <p className="text-muted small mb-0">Audit contested Charge Detail Records (CDRs) raised by drivers, verify telemetry, and issue double-entry wallet refunds.</p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 mt-2 mt-md-0">
          <div className="input-group input-group-sm" style={{ flex: '1 1 200px', maxWidth: 280, minWidth: 0 }}>
            <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
            <input type="text" className="form-control" id="search-cdr" placeholder="Search CDR #, Driver, Session..." aria-label="Search disputes"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="form-select form-select-sm" id="filter-dispute-status" style={{ width: 150 }} aria-label="Filter by dispute status"
            value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="ALL">All Disputes</option>
            <option value="UnderReview">Under Review</option>
            <option value="Adjusted">Adjusted / Refunded</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert" id="dispute-error">{error}</div>}

      <AuditHolds onResolved={(msg) => flash(true, msg)} />

      <div className="row">
        <div className="col-12 col-lg-5 mb-4">
          <div className="card border shadow-sm h-100 bg-white">
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}>
                <i className="fas fa-inbox me-2" style={{ color: '#d97706' }} />Active Customer Disputes Queue
              </h6>
              <span className="badge bg-warning text-dark fw-bold" id="count-pending-disputes">{pending} Pending</span>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush" id="disputes-list-group">
                {items.length === 0 && <div className="p-4 text-center text-muted small">No dispute cases match the search query.</div>}
                {items.map((d) => {
                  const [cls, label] = STATUS_BADGE[d.status];
                  return (
                    <div key={d.id} className={`list-group-item list-group-item-action dispute-item p-3 ${d.id === selectedId ? 'active' : ''}`}
                      data-id={d.id} data-cdr={d.cdrNumber} role="button" tabIndex={0}
                      onClick={() => setSelectedId(d.id)} onKeyDown={(e) => { if (e.key === 'Enter') setSelectedId(d.id); }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-bold text-dark">{d.driverName}</span>
                        <span className={`badge ${cls}`}>{label}</span>
                      </div>
                      <div className="small text-muted mb-1">{d.cdrNumber} &bull; <strong className="text-dark">{php(d.totalAmount)}</strong></div>
                      <p className="small text-muted mb-0 text-truncate">{d.statement}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="card-footer bg-white d-flex justify-content-between align-items-center py-2 px-3">
              <span className="text-muted small">Showing <strong id="dispute-range-start">{rangeStart}</strong>–<strong id="dispute-range-end">{rangeEnd}</strong> of <strong id="dispute-count">{total}</strong> cases</span>
              <nav aria-label="Disputes pagination">
                <ul className="pagination pagination-sm mb-0" id="disputes-pagination">
                  {total > PAGE_SIZE && (
                    <>
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button type="button" className="page-link" aria-label="Previous page" onClick={() => setPage(page - 1)}>&laquo;</button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                          <button type="button" className="page-link" onClick={() => setPage(p)}>{p}</button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button type="button" className="page-link" aria-label="Next page" onClick={() => setPage(page + 1)}>&raquo;</button>
                      </li>
                    </>
                  )}
                </ul>
              </nav>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7 mb-4">
          <div className="card border shadow-sm bg-white">
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0 fw-bold" style={BLUE}>
                <i className="fas fa-file-invoice-dollar me-2" style={BLUE} />CDR Telemetry Audit &amp; Ledger Adjustment
              </h6>
              <span className="badge bg-secondary" id="active-cdr-badge">{detail ? c.cdrNumber : 'Select a dispute'}</span>
            </div>
            <div className="card-body p-4" id="audit-workspace-body">
              {!detail && (
                <div className="text-center py-5 text-muted" id="placeholder-select-dispute">
                  <i className="fas fa-search fa-3x mb-3 text-secondary" />
                  <h5 className="fw-bold text-dark">Select a dispute case from the queue</h5>
                  <p className="small text-muted">Detailed session meter logs, itemized billing breakdown, and refund tools will appear here.</p>
                </div>
              )}
              {detail && (
                <div id="panel-dispute-details">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 border-bottom pb-3 mb-3">
                    <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      <h5 className="fw-bold mb-1" style={BLUE} id="detail-driver-name">{detail.driver.name}</h5>
                      <div className="small text-muted"><span id="detail-driver-email">{detail.driver.email}</span> &bull; <span id="detail-session-id">Session #{detail.session.sessionCode}</span></div>
                      <div className="small text-muted" id="detail-wallet-balance">Wallet balance: {php(detail.driver.walletBalance)}</div>
                    </div>
                    <div className="text-end">
                      <div className="h4 fw-bold mb-0" style={{ color: '#d97706' }} id="detail-total-amount">{php(c.totalAmount)}</div>
                      <span className="badge bg-success fw-bold" id="detail-payment-status">{c.paymentStatus}</span>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="fw-bold text-dark mb-0">Itemized Charge Detail Record (CDR)</h6>
                    <button type="button" className="btn btn-sm btn-outline-primary" id="btn-inspect-telemetry" onClick={openTelemetry}>
                      <i className="fas fa-bolt me-1" /> Inspect Telemetry Logs
                    </button>
                  </div>

                  <div className="bg-light rounded p-3 mb-3 border">
                    <div className="d-flex justify-content-between mb-2 small">
                      <span className="text-muted">Energy Consumption (<span id="detail-energy-kwh">{c.energyKwh}</span> kWh @ {php(c.energyRate)}/kWh):</span>
                      <span className="fw-bold text-dark font-monospace" id="detail-energy-cost">{php(c.energyCost)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 small">
                      <span className="text-muted">Session Initiation Fee:</span>
                      <span className="fw-bold text-dark font-monospace" id="detail-session-fee">{php(c.sessionFee)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 small">
                      <span className="text-muted">Bay Parking Duration:</span>
                      <span className="fw-bold text-dark font-monospace" id="detail-parking-cost">{php(c.parkingCost)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 small">
                      <span className="text-danger fw-bold"><i className="fas fa-triangle-exclamation me-1" />Contested Idle Fee ({c.idleMinutes} mins @ {php(c.idleRatePerMin)}/min):</span>
                      <span className="text-danger fw-bold font-monospace" id="detail-idle-cost">{php(c.idleCost)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 small">
                      <span className="text-muted">VAT (12%, included):</span>
                      <span className="fw-bold text-dark font-monospace" id="detail-vat">{php(c.vat)}</span>
                    </div>
                    <div className="border-top pt-2 d-flex justify-content-between fw-bold">
                      <span className="text-dark">Total Billed:</span>
                      <span className="font-monospace" style={BLUE} id="detail-cdr-total">{php(c.totalAmount)}</span>
                    </div>
                    <div className="small text-muted mt-2" id="detail-stop-reason">
                      Stop reason: {detail.session.stopReason || 'n/a'}{detail.session.anomalyFlag ? ` · Flag: ${detail.session.anomalyFlag}` : ''}{detail.session.chargerId ? ` · Charger ${detail.session.chargerId}` : ''}
                    </div>
                  </div>

                  <div className="alert alert-warning py-2 px-3 mb-3" style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
                    <strong className="d-block small text-uppercase mb-1"><i className="fas fa-comment-alt me-1" />Driver Statement:</strong>
                    <span className="small" id="detail-driver-statement">&ldquo;{detail.driverStatement}&rdquo;</span>
                  </div>

                  <div className="card border">
                    <div className="card-header bg-light py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                      <span className="fw-bold small text-dark"><i className="fas fa-balance-scale me-1 text-success" />Issue Double-Entry Wallet Adjustment</span>
                      <button type="button" className="btn btn-sm btn-outline-secondary" id="btn-view-ledger" onClick={openLedger}>
                        <i className="fas fa-book me-1" /> View General Ledger
                      </button>
                    </div>
                    <div className="card-body p-3 bg-white">
                      {resolved && (
                        <div className={`alert ${detail.status === 'Adjusted' ? 'alert-success' : 'alert-secondary'} py-2 small`} id="dispute-resolution">
                          {detail.status === 'Adjusted'
                            ? <>Adjusted: <strong>{php(detail.resolution.amount)}</strong> ({detail.resolution.category}) credited. Ref <span className="font-monospace">{detail.resolution.transactionReference}</span>.</>
                            : <>Dispute rejected.</>}
                          {' '}Justification: {detail.resolution.justification} &mdash; {detail.resolution.resolvedBy || 'system'}.
                          {detail.notifications.length > 0 && <div id="dispute-notified">Driver notified by {detail.notifications.map((x) => x.channel.toUpperCase()).join(' + ')}{notificationsSimulated ? ' (demo: recorded, not delivered)' : ''}.</div>}
                        </div>
                      )}
                      <div className="row g-2">
                        <div className="col-md-6 mb-2">
                          <label className="small text-muted fw-bold mb-1" htmlFor="adj-type">Adjustment Action</label>
                          <select className="form-select form-select-sm" id="adj-type" value={adjType} disabled={resolved} onChange={(e) => pickType(e.target.value)}>
                            <option value="RefundIdle">Full Refund of Idle Fee ({php(c.idleCost)})</option>
                            <option value="RefundFull">Full Session Waiver ({php(c.totalAmount)})</option>
                            <option value="Custom">Custom Goodwill Credit</option>
                          </select>
                        </div>
                        <div className="col-md-6 mb-2">
                          <label className="small text-muted fw-bold mb-1" htmlFor="adj-amount">Credit Amount (PHP)</label>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text bg-light text-dark fw-bold">₱</span>
                            <input type="number" step="0.01" min="0" className="form-control fw-bold" id="adj-amount" value={amount} disabled={resolved}
                              onChange={(e) => setAmount(e.target.value)} />
                          </div>
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="small text-muted fw-bold mb-1" htmlFor="adj-notes">Auditable Justification Notes</label>
                        <input type="text" className={`form-control form-control-sm ${formError && !notes.trim() ? 'is-invalid' : ''}`} id="adj-notes" maxLength={500}
                          value={notes} disabled={resolved} onChange={(e) => setNotes(e.target.value)} placeholder="Explain the fault and the policy basis for this decision" />
                      </div>
                      {formError && <div className="alert alert-danger py-2 small mb-2" role="alert" id="adj-error">{formError}</div>}
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                        <span className="small text-muted font-monospace">Leg: DEBIT CUSTOMER_SUPPORT_EXPENSE / CREDIT WALLET_PREPAID</span>
                        <div>
                          <button type="button" className="btn btn-sm btn-outline-danger me-2 fw-bold" id="btn-reject-dispute" disabled={resolved || busy} onClick={reject}>Reject Dispute</button>
                          <button type="button" className="btn btn-sm btn-success fw-bold" id="btn-apply-adjustment" disabled={resolved || busy} onClick={apply}>
                            {busy ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="fas fa-check-circle me-1" />} Post Ledger Adjustment
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {telemetry && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="telemetry-title" id="modal-telemetry">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="telemetry-title" style={BLUE}><i className="fas fa-bolt me-2" />Session Meter Logs &mdash; {detail.session.sessionCode}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setTelemetry(null)} />
              </div>
              <div className="modal-body p-3">
                <div className="small text-muted mb-2" id="telemetry-summary">
                  {detail.session.totalEnergyKwh} kWh delivered over {Math.round((detail.session.durationSeconds || 0) / 60)} min · stop reason {detail.session.stopReason || 'n/a'}
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0" id="table-telemetry">
                    <thead><tr className="text-muted small text-uppercase"><th>Time</th><th>Meter (kWh)</th><th>Power (kW)</th><th>Voltage (V)</th><th>Current (A)</th><th>SoC %</th><th>Status</th></tr></thead>
                    <tbody>
                      {telemetry.length === 0 && <tr><td colSpan={7} className="text-center text-muted p-3">No meter readings were recorded for this session.</td></tr>}
                      {telemetry.map((r) => (
                        <tr key={r.readingAt}>
                          <td className="small">{new Date(r.readingAt).toLocaleTimeString('en-PH')}</td><td>{r.meterValueKwh}</td><td>{r.powerKw ?? '—'}</td>
                          <td>{r.voltageV ?? '—'}</td><td>{r.currentA ?? '—'}</td><td>{r.socPercent ?? '—'}</td><td>{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setTelemetry(null)}>Close</button></div>
            </div>
          </div>
        </div>
      )}

      {ledger && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true" aria-labelledby="ledger-title" id="modal-ledger">
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="ledger-title" style={BLUE}><i className="fas fa-book me-2" />General Ledger &mdash; {detail.cdr.cdrNumber}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setLedger(null)} />
              </div>
              <div className="modal-body p-3">
                <div className="table-responsive">
                  <table className="table table-sm mb-0" id="table-ledger">
                    <thead><tr className="text-muted small text-uppercase"><th>Reference</th><th>Leg</th><th>Account</th><th className="text-end">Amount</th><th>Description</th></tr></thead>
                    <tbody>
                      {ledger.length === 0 && <tr><td colSpan={5} className="text-center text-muted p-3">No ledger postings exist for this case yet.</td></tr>}
                      {ledger.map((l, i) => (
                        <tr key={`${l.transactionReference}-${l.accountType}-${l.entryType}-${i}`}>
                          <td className="small font-monospace">{l.transactionReference}</td><td>{l.entryType}</td><td className="small">{l.accountType}</td>
                          <td className="text-end font-monospace">{php(l.amount)}</td><td className="small text-muted">{l.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setLedger(null)}>Close</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
